import { Link } from 'react-router-dom'
import type { Theme } from '@/types'
import { SmartImage } from './SmartImage'

interface ThemeRailProps {
  themes: Theme[]
  showViewAll?: boolean
}

export default function ThemeRail({ themes, showViewAll = true }: ThemeRailProps) {
  return (
    <div className="relative">
      {/* Horizontal scroll container */}
      <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-thin">
        {themes.map((theme) => (
          <Link
            key={theme.slug}
            to={`/themes/${theme.slug}`}
            className="flex-shrink-0 w-64 snap-start group"
          >
            <div className="artifact-frame h-full">
              {/* Theme image */}
              <div className="aspect-[3/2] bg-archive-slate overflow-hidden">
                {theme.hero_image_url ? (
                  <SmartImage
                    src={theme.hero_image_url}
                    alt={theme.title}
                    sizes="(min-width: 1024px) 387px, 256px"
                    width={387}
                    height={258}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-4xl text-archive-ash font-display">{theme.title[0]}</span>
                  </div>
                )}
              </div>
              {/* Content */}
              <div className="p-4">
                <h3 className="font-display text-archive-cream group-hover:text-archive-gold transition-colors">
                  {theme.title}
                </h3>
                <p className="mt-1 text-sm text-archive-muted line-clamp-2">
                  {theme.description}
                </p>
              </div>
            </div>
          </Link>
        ))}

        {/* View all card */}
        {showViewAll && (
          <Link
            to="/themes"
            className="flex-shrink-0 w-64 snap-start group"
          >
            <div className="artifact-frame h-full flex items-center justify-center min-h-[200px]">
              <div className="text-center p-4">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-archive-gold/10 flex items-center justify-center border border-archive-gold/30 group-hover:border-archive-gold transition-colors">
                  <svg className="w-6 h-6 text-archive-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
                <span className="font-display text-archive-cream group-hover:text-archive-gold transition-colors">
                  View All Themes
                </span>
              </div>
            </div>
          </Link>
        )}
      </div>

      {/* Fade edges */}
      <div className="absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-archive-black to-transparent pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-archive-black to-transparent pointer-events-none" />
    </div>
  )
}
